/**
 * Thin wrappers for each SmartImprove admin config entity.
 *
 * Each wrapper feeds `<SmartImproveConfigPage>` a per-entity field config +
 * column config + API binding. Adding a new library later is ~20 lines —
 * no duplicated table or dialog code.
 */
import { useEffect, useState, useMemo } from "react";
import SmartImproveConfigPage from "./SmartImproveConfigPage";
import {
  operationalAreasApi,
  playbookApi,
} from "../../lib/smartimproveAdminApi";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */
function useOperationalAreaOptions() {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    operationalAreasApi
      .list()
      .then((rows) =>
        setOpts(
          (rows || [])
            .filter((r) => r.active !== false)
            .map((r) => ({ value: r.key, label: r.label }))
        )
      )
      .catch(() => setOpts([]));
  }, []);
  return opts;
}

const COMPLEXITY = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const TREND = [
  { value: "higher_is_better", label: "Higher is better" },
  { value: "lower_is_better", label: "Lower is better" },
  { value: "neutral", label: "Neutral" },
];

const ROI_RELEVANCE = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

/* ------------------------------------------------------------------ */
/* Business Processes (formerly "Operational Areas")                  */
/* ------------------------------------------------------------------ */
/* The Business Processes admin page is now a dedicated component     */
/* with inline sub-process management. See                            */
/* `/app/frontend/src/admin/smartimprove/BusinessProcessesPage.jsx`.  */
/* This file no longer exports a `OperationalAreasPage` wrapper —     */
/* App.js imports the new accordion-style page directly.              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Issue Templates (formerly "Issue Library")                         */
/* ------------------------------------------------------------------ */
/* The Issue Templates admin page is now a dedicated component with   */
/* consulting-grade fields (Business Process + Sub-Process mapping,   */
/* Typical Symptoms, Business Impact, KPI Impact, Industry            */
/* Applicability, Investigation Areas).                               */
/* See `/app/frontend/src/admin/smartimprove/IssueTemplatesPage.jsx`. */
/* App.js imports the new page directly.                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* KPI Templates (formerly "KPI Library")                             */
/* ------------------------------------------------------------------ */
/* The KPI Templates admin page is now a dedicated consulting-grade   */
/* component with the full intelligence framework (Business Process + */
/* Sub-Process, KPI Category, Business Impact Area, Executive         */
/* Importance, Industry Applicability, Typical Improvement %).        */
/* See `/app/frontend/src/admin/smartimprove/KpiTemplatesPage.jsx`.   */
/* App.js imports the new page directly.                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Insight Templates (formerly "Insight Engine")                      */
/* ------------------------------------------------------------------ */
/* The Insight Templates admin page is now a dedicated consulting-    */
/* grade component with the full intelligence framework (Business     */
/* Process + Sub-Process, Insight Category, Transformation Theme,     */
/* Executive Severity, Business Impact, Operational Implication,      */
/* Executive Summary Snippet, Rollout Wave Hint).                     */
/* See `/app/frontend/src/admin/smartimprove/InsightTemplatesPage.jsx`. */
/* App.js imports the new page directly.                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Recommendation Templates (consulting-grade "Recommendation Engine")*/
/* ------------------------------------------------------------------ */
/* The Recommendation Templates admin page is now a dedicated         */
/* consulting-grade component with the full transformation framework  */
/* (Business Process + Sub-Process, Transformation Theme, Rollout     */
/* Wave, Complexity, Business Impact Area, Operational Benefit,       */
/* Executive Summary Snippet, Implementation Approach, Solution       */
/* Enablement Options across SAP / PrimeS4 / Future buckets).         */
/* See `/app/frontend/src/admin/smartimprove/RecommendationTemplatesPage.jsx`. */
/* App.js imports the new page directly.                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* ROI Models                                                         */
/* ------------------------------------------------------------------ */
/* The ROI Models admin page is now a dedicated consulting-grade      */
/* component — a lightweight benchmark-driven ROI Assumption Engine   */
/* (Productivity, Inventory, Automation, Cost, Investment, Confidence)*/
/* with sectioned cards and per-engagement override semantics.        */
/* See `/app/frontend/src/admin/smartimprove/RoiModelsPage.jsx`.      */
/* App.js imports the new page directly.                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Implementation Templates (consulting-grade execution programs)     */
/* ------------------------------------------------------------------ */
/* The Implementation Templates admin page is now a dedicated         */
/* consulting-grade card-based component with the full execution      */
/* framework (Rollout Strategy + Rollout Model + Transformation Theme,*/
/* Rollout Groups, Phases, Resources, Weekly Effort Matrix).          */
/* See `/app/frontend/src/admin/smartimprove/ImplementationTemplatesPage.jsx`. */
/* App.js imports the new page directly.                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Consultant Assignment (DEPRECATED — Feb 2026)                      */
/* ------------------------------------------------------------------ */
/* Removed from the admin UI to simplify the SmartImprove admin       */
/* surface area. Backend collection (`smartimprove_assignment_rules`) */
/* and CRUD route (`/api/admin/smartimprove/assignment-rules`) remain */
/* dormant for backward compatibility — existing rows are preserved   */
/* but no longer exposed via navigation.                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/* Improvement Knowledge Library (formerly "SmartImprove Playbook")   */
/* ------------------------------------------------------------------ */
/* Repository for operational standards, templates, checklists, and   */
/* guidance articles. Storage layer (`playbookApi`) is UNCHANGED —     */
/* only the page identity was renamed; the consultant methodology now  */
/* lives in the separate SmartImprovePlaybookPage.                     */
export function KnowledgeLibraryPage() {
  const fields = [
    { key: "slug", label: "Slug", required: true },
    { key: "title", label: "Title", required: true, span: "full" },
    { key: "category", label: "Category", placeholder: "delivery" },
    { key: "body", label: "Body", type: "textarea", rows: 6, span: "full" },
    { key: "tags", label: "Tags", type: "list", span: "full" },
  ];
  const columns = [
    { key: "title", label: "Title" },
    { key: "category", label: "Category" },
    { key: "tags", label: "Tags", type: "list" },
  ];
  return (
    <SmartImproveConfigPage
      title="Improvement Knowledge Library"
      description="Repository of operational standards, templates, checklists, and guidance articles for SmartImprove engagements — delivery norms, analysis protocols, workshop kit."
      entityLabel="Knowledge Entry"
      testId="si-knowledge-library"
      fields={fields}
      columns={columns}
      api={playbookApi}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Narrative Guidance (formerly "AI Guidance Rules")                  */
/* ------------------------------------------------------------------ */
/* The Narrative Guidance admin page is now a dedicated consulting-   */
/* grade component that governs insight tone, recommendation framing, */
/* executive-summary style, and consulting narrative consistency.     */
/* Storage layer preserved (`smartimprove_ai_guidance` + `/ai-        */
/* guidance` route slug) for backward compatibility with the          */
/* deterministic narrative engine.                                    */
/* See `/app/frontend/src/admin/smartimprove/NarrativeGuidancePage.jsx`. */
/* App.js imports the new page directly.                              */
/* ------------------------------------------------------------------ */
